import { Injectable } from '@angular/core';
import { of, finalize } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ApiService } from '../api-service/api.service';
import { Apis } from '../API';
import { CookieService } from '../cookie-service/cookie.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private cookieService: CookieService,
    private router: Router,
  ) { }

  getPublicKey(): Observable<any> {
    return this.apiService.get(Apis.authentification.public_key);
  }

  loginUser(body: {
    name: string;
    password: string;
    code_role: 'ADHE';
    deviceInfo: { 
      browser_name: string;
      browser_version: string;
      os_name: string;
      os_version: string;
      device_type: string;
    }
  }) {
    return this.apiService.post(Apis.authentification.login, body);
  }

  chooseOTPMethod(body: {
    sessionId: string;
    accountHash: string;
    verificationMethod: string;
    deviceInfo: {
      browser_name: string;
      browser_version: string;
      os_name: string;
      os_version: string;
      device_type: string;
    }
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.chooseOTPMethod, body);
  }

  LogOutUser() {
    return this.apiService.post(`${Apis.authentification.logout}`, {});
  }

// === ACCOUNT REGISTRATION METHODS ===
  registerAccount(body: {
    matricule: string;
    cin: string;
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.register, body);
  }

  // UPDATED: verifyOTP now accepts SMS OTP code only
  verifyOTP(body: {
    userId?: number;
    matricule?: string;
    cin?: string;
    otpCodeSMS: string;
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.verifyOTP, body);
  }

  // Verify Email OTP
  verifyEmailOTP(body: {
    userId?: number;
    matricule?: string;
    cin?: string;
    otpCodeEmail: string;
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.verifyOTP, body);
  }

  // SIMPLIFIED: Complete registration with token in body
  completeRegistration(body: {
    token: string;
    password: string;
    confirmPassword: string;
  }): Observable<any> {
    // The token is now correctly expected in the body by the backend.
    // apiService.post should handle sending the 'body' object as JSON.
    return this.apiService.post(Apis.authentification.completeRegistration, body);
  }

  // Helper method to set CREAT token for registration completion
  setRegistrationToken(token: string): void {
    sessionStorage.setItem('creat_token', token);
  }

  // Helper method to get CREAT token for registration completion
  getRegistrationToken(): string | null {
    return sessionStorage.getItem('creat_token');
  }

  // Helper method to clear CREAT token after use
  clearRegistrationToken(): void {
    sessionStorage.removeItem('creat_token');
  }

  // === RESEND REGISTRATION OTP ===
  resendRegistrationOTP(body: { matricule: string; cin: string }): Observable<any> {
    return this.apiService.post(Apis.authentification.renvoieSMSActiverTelephone, body);
  }

  // Resend Email OTP for activation
  resendEmailOTP(body: { matricule: string; cin: string; email: string }): Observable<any> {
    return this.apiService.post(Apis.authentification.renvoieEmailActivation, body);
  }

  // === PASSWORD RESET FLOW ===
  forgotPassword(body: {
    matricule: string;
  }): Observable<any> {
    return this.apiService.post(
      `${Apis.authentification.forgotPassword}`,
      body,
    );
  }

  // SIMPLIFIED: verifyResetOTP returns just token (no signature)
  verifyResetOTP(body: {
    matricule?: string;
    email?: string;
    userId?: number;
    otpCode: string;
  }): Observable<any> {
    return this.apiService.post(
      `${Apis.authentification.verifyResetOTP}`,
      body,
    );
  }

  // SIMPLIFIED: Reset password with token in body
  resetPasswordWithToken(body: {
    token: string;
    newPassword: string;
    confirmPassword: string;
  }): Observable<any> {
    // The token is now correctly expected in the body by the backend.
    // apiService.post should handle sending the 'body' object as JSON.
    return this.apiService.post(Apis.authentification.resetPassword, body);
  }

  // Helper method to set RESET token for password reset
  setResetToken(token: string): void {
    sessionStorage.setItem('reset_token', token);
  }

  // Helper method to get RESET token for password reset
  getResetToken(): string | null {
    return sessionStorage.getItem('reset_token');
  }

  // Helper method to clear RESET token after use
  clearResetToken(): void {
    sessionStorage.removeItem('reset_token');
  }

  // === RESEND OTP METHODS ===
  resendOTPEmail(body: { matricule: string }): Observable<any> {
    return this.apiService.post(Apis.authentification.resendOTPEmail, body);
  }

  resendOTPSMS(body: { matricule: string }): Observable<any> {
    return this.apiService.post(Apis.authentification.resendOTPSMS, body);
  }

  // === ACCOUNT MANAGEMENT METHODS ===
  // Email change methods (3-step flow with cross-verification)
  initiateEmailChange(newEmail: string): Observable<any> {
    return this.apiService.post(Apis.authentification.emailInitChange, {
      newEmail,
    });
  }

  verifyEmailChangeStep1(body: {
    flowToken: string;
    otpCode: string;
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.emailVerifyCurrentContact, body);
  }

  verifyEmailChangeStep2(body: {
    flowToken: string;
    otpCode: string;
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.emailVerifyNewContact, body);
  }

  // Phone change methods (3-step flow with cross-verification)
  initiatePhoneChange(newPhone: string): Observable<any> {
    return this.apiService.post(Apis.authentification.phoneInitChange, {
      newPhone,
    });
  }

  verifyPhoneChangeStep1(body: {
    flowToken: string;
    otpCode: string;
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.phoneVerifyCurrentContact, body);
  }

  verifyPhoneChangeStep2(body: {
    flowToken: string;
    otpCode: string;
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.phoneVerifyNewContact, body);
  }

  // Password change method
  changePassword(body: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.changePassword, body);
  }

  // SIMPLIFIED: validateRegistrationToken with just token
  validateRegistrationToken(body: { token: string; tokenType: string }): Observable<any> {
    return this.apiService.post(Apis.authentification.validateToken, body);
  }

  verifyLoginOTP(body: {
    sessionId: string;
    otpCode: string;
    accountHash: string;
    saveDevice?: boolean;
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.verifyLoginOTP, body);
  }

  // NEW METHOD - Verify first-time login OTP (legacy - dual OTP)
  verifyFirstTimeOTP(body: {
    userId: string;
    sessionId: string;
    otpEmail: string;
    otpPhone: string;
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.verifyFirstTimeOTP, body);
  }

  // === FIRST-TIME LOGIN 4-STEP FLOW ===

  // Step 2-0: Send phone OTP for first-time login
  sendFirstTimePhoneOTP(body: {
    userId: string;
    deviceInfo: {
      browser_name: string;
      browser_version: string;
      os_name: string;
      os_version: string;
      device_type: string;
    }
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.sendFirstTimePhoneOTP, body);
  }

  // Step 2-1: Verify phone OTP for first-time login
  verifyFirstTimePhoneOTP(body: {
    userId: string;
    otpPhone: string;
    sessionId: string;
    deviceInfo: {
      browser_name: string;
      browser_version: string;
      os_name: string;
      os_version: string;
      device_type: string;
    }
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.verifyFirstTimePhoneOTP, body);
  }

  // Step 3-0: Send email OTP for first-time login
  sendFirstTimeEmailOTP(body: {
    userId: string;
    deviceInfo: {
      browser_name: string;
      browser_version: string;
      os_name: string;
      os_version: string;
      device_type: string;
    }
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.sendFirstTimeEmailOTP, body);
  }

  // Step 3-1: Verify email OTP and activate account (flag_start = 'O')
  verifyFirstTimeEmailOTP(body: {
    userId: string;
    otpEmail: string;
    sessionId: string;
    deviceInfo: {
      browser_name: string;
      browser_version: string;
      os_name: string;
      os_version: string;
      device_type: string;
    }
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.verifyFirstTimeEmailOTP, body);
  }

  // Step 3-0 (Resend): Resend email OTP for first-time login
  resendFirstTimeEmailOTP(body: {
    userId: string;
    deviceInfo: {
      browser_name: string;
      browser_version: string;
      os_name: string;
      os_version: string;
      device_type: string;
    }
  }): Observable<any> {
    return this.apiService.post(Apis.authentification.resendFirstTimeEmailOTP, body);
  }
}